import React, { useState, useEffect, useCallback, useRef } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import { pick } from 'ramda';
import { Stack, Select, Button, Text, Checkbox, Overlay, LoadingBalls } from '@moda/om';
import { useForm } from '@moda/portal-stanchions';
import { logger } from '../../../lib/logger';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { getNetworkErrorMessage, isGenericError } from '../../../lib/error';
import { useUser } from '../../../hooks/useUser';
import { usePaymentInfoEnteredTracking } from '../../../hooks/useCheckout';
import { useAddressesQuery, RestAddress } from '../../../hooks/useAddress';
import { useFormValidationTracking } from '../../../hooks/useCheckout/useFormValidationTracking';
import {
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  PaymentMethod,
  BillingAddress
} from '../../../hooks/usePaymentMethods';
import { useAddressState } from '../../ShippingAddress/ShippingAddressForm/useAddressState';
import { getValidations as getAddressValidations } from '../../ShippingAddress/ShippingAddressForm/getValidations';
import { ShippingAddressFormFields } from '../../ShippingAddress';
import { Loading } from '../../Loading';
import { usePaymentError } from '../../../hooks/useCheckout/usePaymentError';
import { BraintreeCardFormHandle, CardData } from './PaymentMethodBraintreeCardForm';
import { humanizeAddress } from './humanizeAddress';
import { convertBillingAddressToShippingAddress } from './convertBillingAddressToShippingAddress';
import { convertShippingAddressToBillingAddress } from './convertShippingAddressToBillingAddress';

import './PaymentMethodForm.scss';

const PaymentMethodBraintreeCardForm = loadable(() => import('./PaymentMethodBraintreeCardForm'), {
  resolveComponent: mod => mod.PaymentMethodBraintreeCardForm,
  fallback: (
    <Overlay show>
      <LoadingBalls />
      <Text treatment="eyebrow">Loading</Text>
    </Overlay>
  )
});

const ADDRESS_VALIDATION_FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'postalCode'];

enum Mode {
  Resting,
  Saving
}

interface Props {
  amount?: string;
  method?: PaymentMethod;
  submitButtonLabel?: string;
  confirmCVV?: boolean;
  allowUsingExistingAddress?: boolean;
  onCancel?: () => void;
  onBeforeSave?: (args: CardData & { billingAddress: BillingAddress | null }) => boolean;
  onSave?: (method?: PaymentMethod) => void | Promise<void>;
}

type FormValues = {
  addressId: string;
  cardholderName: string;
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
};

export const PaymentMethodForm: React.FC<Props> = ({
  amount,
  method,
  submitButtonLabel = 'Save',
  allowUsingExistingAddress = true,
  onCancel,
  onBeforeSave,
  onSave
}) => {
  const billingAddress = method?.attributes.billingAddress;

  const { user } = useUser();
  const { paymentError, setPaymentError } = usePaymentError();

  const [mode, setMode] = useState(Mode.Resting);

  const braintreeFormRef = useRef<BraintreeCardFormHandle>(null);

  const [errorMessage, setErrorMessage] = useState('');

  const [useExistingAddress, setUseExistingAddress] = useState(Boolean(billingAddress?.id));

  const [shippingAddressAttributes, setShippingAddressAttributes] = useAddressState(
    !billingAddress || billingAddress?.id
      ? undefined
      : convertBillingAddressToShippingAddress(billingAddress)
  );

  const { addresses, error, loading } = useAddressesQuery({ skip: !allowUsingExistingAddress });

  const [createPaymentMethod] = useCreatePaymentMethodMutation();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();
  const { trackPaymentInfoEntered } = usePaymentInfoEnteredTracking();
  const { trackFormValidation } = useFormValidationTracking();

  const onSubmit = useCallback(
    async (fields: FormValues) => {
      if (!braintreeFormRef.current?.validate()) return;

      setMode(Mode.Saving);
      setErrorMessage('');
      if (paymentError) setPaymentError(null);

      try {
        const address = fields.addressId
          ? addresses.find(address => address.id === fields.addressId)
          : ({
              __typename: 'RestAddress',
              id: '',
              type: 'addresses',
              attributes: {
                __typename: 'RestAddressAttributes',
                verified: true,
                source: 'moda',
                firstName: '',
                lastName: '',
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                postalCode: '',
                phone: '',
                countryCode: '',
                ...fields
              }
            } as RestAddress);

        if (!address) throw new Error('PaymentMethodForm: address not found');

        const billingAddress = convertShippingAddressToBillingAddress(address);

        const braintreeFormArgs = await braintreeFormRef.current?.submit({
          address: billingAddress
        });

        const commonArgs = {
          cardholderName: braintreeFormArgs?.cardholderName ?? '',
          nonce: braintreeFormArgs?.nonce ?? '',
          threeDSecureAuthenticationId: braintreeFormArgs?.threeDSecureAuthenticationId ?? '',
          billingAddress
        };

        if (onBeforeSave && !onBeforeSave({ ...braintreeFormArgs, billingAddress })) return;

        const result = method?.id
          ? (
              await updatePaymentMethod({
                id: method.id,
                ...commonArgs
              })
            ).data?.updatePaymentMethod.data
          : (
              await createPaymentMethod({
                ...commonArgs
              })
            ).data?.createPaymentMethod.data;

        await onSave?.(result);
        trackPaymentInfoEntered(result);
      } catch (error) {
        if (!isGenericError(error)) return;

        const networkErrorMessage = getNetworkErrorMessage(error);
        const message =
          networkErrorMessage || error.displayMessage || 'An error occurred. Please retry later.';

        setErrorMessage(message);
        reportError('PAYMENT_METHOD_FORM_SUBMIT', error);

        logger.info('PAYMENT_METHOD_FAILED', error.toString(), {
          error: error.toString(),
          networkErrorMessage,
          message,
          email: user.email,
          guest: !user.isLoggedIn
        });
      }
      setMode(Mode.Resting);
    },
    [
      paymentError,
      setPaymentError,
      addresses,
      method,
      updatePaymentMethod,
      createPaymentMethod,
      onSave,
      trackPaymentInfoEntered,
      user,
      onBeforeSave
    ]
  );

  const { handleSubmit, errors, hasErrors } = useForm({
    onSubmit,
    validations: useExistingAddress
      ? {}
      : pick(
          ADDRESS_VALIDATION_FIELDS as keyof typeof getAddressValidations,
          getAddressValidations(shippingAddressAttributes)
        )
  });

  useEffect(() => {
    if (addresses.length > 0 && !method) setUseExistingAddress(true);
  }, [addresses, method, setUseExistingAddress]);

  useEffect(() => {
    if (hasErrors) trackFormValidation('Payment Method Form Validation Error', errors);
  }, [hasErrors, errors, trackFormValidation]);

  if (error) throwQueryError(error);

  return (
    <>
      <form
        className={classNames('PaymentMethodForm', { 'PaymentMethodForm--error': errorMessage })}
        onSubmit={event => {
          event.preventDefault();
          handleSubmit(event);
          braintreeFormRef.current?.validate();
        }}
        id="BraintreePaymentMethodForm"
      >
        <Stack space={4}>
          <PaymentMethodBraintreeCardForm ref={braintreeFormRef} amount={amount} method={method} />
          <div className="PaymentMethodForm__divider" />
          {loading && <Loading />}
          {!loading && (
            <>
              <Text treatment="eyebrow">Billing Address</Text>
              {addresses.length > 0 && (
                <Checkbox
                  checked={useExistingAddress}
                  onChange={event => setUseExistingAddress(event.target.checked)}
                >
                  Use an existing address
                </Checkbox>
              )}
              {useExistingAddress && (
                <Select
                  name="addressId"
                  options={addresses.map(address => ({
                    label: humanizeAddress(address),
                    value: address.id
                  }))}
                  defaultValue={
                    billingAddress?.id &&
                    addresses.some(address => address.id === billingAddress.id)
                      ? billingAddress.id
                      : addresses[0]?.id
                  }
                />
              )}

              {!useExistingAddress && (
                <ShippingAddressFormFields
                  address={shippingAddressAttributes}
                  onChange={setShippingAddressAttributes}
                  version="compact"
                  mutationFieldErrors={undefined}
                  validationErrors={errors}
                />
              )}
            </>
          )}

          {errorMessage && (
            <Text treatment="body1" color="code-red">
              {errorMessage}
            </Text>
          )}

          <Stack space={2} direction="horizontal" justifyContent="flex-end">
            {onCancel && (
              <Button
                className="PaymentMethodForm__button"
                secondary
                type="button"
                onClick={onCancel}
                data-testid="PaymentMethodForm__button"
              >
                Cancel
              </Button>
            )}
            <Button className="PaymentMethodForm__button" type="submit">
              {submitButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </form>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
